<template>
  <!-- XOL-602/603/604 (D4): "Account e-mails", the history of the e-mails the app sent ABOUT this
       account (invitation, reset link, "password changed" notice). A super only: GET
       contacts/{id}/account-mail (#5) is super-only, and a View Only super may read.

       It is the BODY of a slider drawer (Popup, with a Close-only footer) that ContactDetail opens
       from the "View captured e-mails" / "View account e-mails" link beside the send button on
       System Details (Carlos, 2026-10-19). It used to be an inline section below the form. The
       drawer's heading is the title, so this has none of its own: the section is labelled for a
       screen reader instead.

       The drawer renders its body on the first open, so this component is created, and reads the
       list, then; every later open calls refresh() (ContactDetail), and so does a send.

       It is INDEPENDENT of the send action. PasswordLinkAction reads its own endpoint; this
       list's states (loading, error, 403, 404) affect only this list.

       On a capture host (`mail_mode: 'capture'`) a row's "View e-mail" opens MailPreviewDialog
       with this contact's stored copy, stacked over the drawer. A 404 from the API (an older one)
       emits `hidden`, and ContactDetail removes the link (and with it this drawer). -->
  <section v-if="state !== 'hidden'" class="account-mail" :aria-label="copy('list.heading')">

    <div class="account-mail__head">
      <span></span>
      <!-- Phone width: icon-only, so it keeps its aria-label. -->
      <el-button v-if="isMediumOrLess" type="text" size="mini" icon="fa fa-sync-alt" :disabled="loading"
        :aria-label="copy('list.refreshAria')" @click="refresh" />
      <el-button v-else class="btn-icon-gap" type="text" size="mini" icon="fa fa-sync-alt" :disabled="loading"
        :aria-label="copy('list.refreshAria')" @click="refresh">{{copy('list.refresh')}}</el-button>
    </div>

    <div class="account-mail__body" :class="{ 'is-loading': loading }" v-loading="loading" :element-loading-text="copy('list.loading')">

      <template v-if="state === 'error'">
        <el-alert type="error" show-icon :closable="false" :title="copy('list.error')" />
        <el-button size="small" class="account-mail__retry" @click="refresh">{{copy('list.retry')}}</el-button>
      </template>

      <el-alert v-else-if="state === 'forbidden'" type="info" show-icon :closable="false" :title="copy('list.forbidden')" />

      <template v-else-if="state === 'ready'">

        <el-alert v-if="mailMode === 'capture'" class="account-mail__notice" type="info" show-icon :closable="false"
          :title="copy('list.capture.title')" :description="copy('list.capture.description')" />
        <el-alert v-else-if="mailMode === 'redirect'" class="account-mail__notice" type="info" show-icon :closable="false"
          :title="copy('list.redirect.title')" :description="copy('list.redirect.description')" />

        <p v-if="rows.length === 0" class="account-mail__empty">{{copy('list.empty')}}</p>

        <template v-else>
          <p v-if="hasQueued" class="mail-batch__queued">
            <small>{{copy('list.queued')}}</small>
            <el-button type="text" size="mini" icon="fa fa-sync-alt" :disabled="loading" @click="refresh">{{copy('list.refresh')}}</el-button>
          </p>

          <el-table class="mail-batch__table" size="small" :data="rows" :row-class-name="rowClass" style="width: 100%">

            <!-- One column on a phone: no minimum, or the column outgrows the drawer and the cell clips. -->
            <el-table-column :label="copy('list.col.email')" :min-width="isMediumOrLess ? 0 : 260">
              <template slot-scope="scope">
                <b class="mail-batch__email">{{scope.row.kindText}}</b>
                <small class="mail-batch__line mail-batch__org">{{scope.row.whoText}}</small>
                <small class="mail-batch__line mail-batch__aux">{{scope.row.toText}}</small>
                <small v-if="scope.row.redirectText" class="mail-batch__line mail-batch__aux">{{scope.row.redirectText}}</small>

                <!-- v-if, never v-show: a row without a stored copy (or a viewer) has no button. -->
                <div v-if="scope.row.canView" class="mail-batch__preview">
                  <el-button type="text" size="mini" icon="fa fa-eye" aria-haspopup="dialog"
                    :aria-label="scope.row.viewAria"
                    @click="openPreview(scope.row, $event)">{{copy('preview.view')}}</el-button>
                </div>

                <!-- Phone width: the table has one column; the status stacks under the address. -->
                <div v-if="isMediumOrLess" class="mail-status mail-status--stacked">
                  <el-tag size="mini" effect="light" :type="scope.row.tag.type"><i class="fa" :class="scope.row.tag.icon" aria-hidden="true"></i><span>{{scope.row.tag.text}}</span></el-tag>
                  <small class="mail-status__detail">{{scope.row.detail}}</small>
                  <small v-if="scope.row.linkText" class="mail-status__detail is-block">{{scope.row.linkText}}</small>
                </div>
              </template>
            </el-table-column>

            <el-table-column v-if="!isMediumOrLess" key="status" :label="copy('list.col.status')" min-width="180">
              <template slot-scope="scope">
                <div class="mail-status">
                  <el-tag size="mini" effect="light" :type="scope.row.tag.type"><i class="fa" :class="scope.row.tag.icon" aria-hidden="true"></i><span>{{scope.row.tag.text}}</span></el-tag>
                  <small class="mail-status__detail">{{scope.row.detail}}</small>
                  <small v-if="scope.row.linkText" class="mail-status__detail is-block">{{scope.row.linkText}}</small>
                </div>
              </template>
            </el-table-column>

          </el-table>

          <small v-if="rows.length >= 20" class="mail-batch__aux account-mail__recent">{{copy('list.recent')}}</small>
          <p class="info-footnote"><i class="fa fa-info-circle" aria-hidden="true"></i><span>{{copy('list.helper')}}</span></p>
        </template>

      </template>
    </div>

    <!-- The stored copy, stacked over the contact's drawer. Mounted CLOSED and opened on the next
         tick (see MailBatchDialog): a dialog mounted already visible never emits @open/@opened,
         so it would neither fade in nor take focus. v-if: nothing exists until a click. -->
    <mail-preview-dialog v-if="previewShown"
      :visible="!!previewRow"
      :preview-path="previewPath"
      copy-prefix="accountMail.preview"
      :link-notice="previewShown.kind !== 'password_changed'"
      :email="previewShown.email"
      :delivered-to="previewShown.delivered_to"
      :label="previewShown.label"
      :recipient-line="recipientLine"
      @close="previewRow = null"
      @closed="previewLast = null" />

  </section>
</template>

<script>
import axios from '@/lib/axiosClient'
import DateMixins from '@/mixins/DateMixins'
import MailPreviewDialog from '@/components/requests/MailPreviewDialog'
import { activityText } from '@/lib/requestActivity'
import { errStatus } from '@/lib/linkErrors'

// A row is a "link" e-mail unless it is the notice, which has no link and so no link status.
var KINDS = ['invite', 'reset_admin', 'reset_self', 'password_changed']
var LINK_STATUSES = ['pending', 'used', 'expired', 'superseded', 'cancelled']

export default {
  name: 'account-mail-list',

  mixins: [DateMixins],

  components: {
    MailPreviewDialog
  },

  props: {
    // The SAVED contact (ContactDetail's initialForm): id, name, login. The preview's header
    // names them.
    contact: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      // 'loading' | 'ready' | 'error' | 'forbidden' | 'hidden' (a 404: an older API)
      state: 'loading',
      loading: false,
      mailMode: null,
      items: [],
      seq: 0,
      previewRow: null,
      previewLast: null
    }
  },

  computed: {
    contactId () {
      return this.contact ? this.contact.id : null
    },

    rows () {
      var self = this

      return this.items.map(function (item) {
        return self.row(item)
      })
    },

    hasQueued () {
      return this.items.some(function (item) {
        return item.status === 'queued'
      })
    },

    // What the dialog shows: outlives previewRow so the title does not empty while it animates out.
    previewShown () {
      return this.previewRow || this.previewLast
    },

    previewPath () {
      return this.previewShown ? 'contacts/' + this.contactId + '/account-mail/' + this.previewShown.id + '/preview' : null
    },

    // "Aisha Rahman · Login ID arahman"
    recipientLine () {
      var c = this.contact || {}

      return this.copy('preview.recipientLine', { name: c.name, login: c.login })
    }
  },

  watch: {
    contactId () {
      this.previewRow = null
      this.previewLast = null
      this.refresh()
    }
  },

  created () {
    this.refresh()
  },

  beforeDestroy () {
    this.seq++
  },

  methods: {
    copy (key, params) {
      return activityText('accountMail.' + key, params)
    },

    // GET #5. Also what ContactDetail calls after the send action says `sent`.
    refresh () {
      var self = this
      var seq = ++this.seq

      if (!this.contactId) {
        this.fail('hidden')
        return Promise.resolve()
      }

      // The first load shows the loading state; a refresh keeps the rows under the mask.
      this.loading = true

      return axios.get(this.getEndpoint('contacts/' + this.contactId + '/account-mail', null, false)).then(function (response) {
        if (seq !== self.seq) {
          return
        }

        var body = response ? response.data : null

        if (!body || !Array.isArray(body.items)) {
          self.fail('error')
          return
        }

        self.mailMode = body.mail_mode
        self.items = body.items
        self.state = 'ready'
        self.loading = false
        self.dropMissingPreview()
      }, function (err) {
        if (seq !== self.seq) {
          return
        }

        var status = errStatus(err)

        self.fail(status === 404 ? 'hidden' : (status === 403 ? 'forbidden' : 'error'))
      })
    },

    fail (state) {
      this.state = state
      this.loading = false
      this.items = []
      this.previewRow = null

      // A 404 is an older API with no such endpoint: there is nothing to open.
      if (state === 'hidden') {
        this.$emit('hidden')
      }
    },

    // A refresh that no longer lists the row whose e-mail is open closes that e-mail.
    dropMissingPreview () {
      var row = this.previewRow

      if (!row) {
        return
      }

      var still = this.items.some(function (item) {
        return String(item.id) === String(row.id)
      })

      if (!still) {
        this.previewRow = null
      }
    },

    rowClass (ctx) {
      return ctx.row.status === 'failed' ? 'is-failed' : ''
    },

    // One ledger item -> what the row shows.
    row (item) {
      var kind = KINDS.indexOf(item.kind) !== -1 ? item.kind : null
      var created = item.created_at ? this.formatDateTime(item.created_at) : ''
      var kindText = kind ? this.copy('kind.' + kind) : activityText('requestMail.label.other')
      var delivered = item.delivered_to && String(item.delivered_to).trim().toLowerCase() !== String(item.email || '').trim().toLowerCase() ? item.delivered_to : null
      var canView = item.has_preview === true && !this.profile().viewer

      return {
        id: item.id,
        kind: kind,
        status: item.status,
        email: item.email,
        delivered_to: item.delivered_to || null,
        label: kind ? this.copy('kind.label.' + kind) : null,
        kindText: kindText,
        whoText: this.whoText(item, kind, created),
        toText: this.copy('list.to', { email: item.email }),
        redirectText: delivered ? activityText('requestMail.detail.redirect', { address: delivered }) : '',
        canView: canView,
        viewAria: this.copy('preview.viewAria', { kind: kindText.toLowerCase(), datetime: created }),
        tag: this.tag(item),
        detail: this.detail(item),
        linkText: kind && kind !== 'password_changed' && LINK_STATUSES.indexOf(item.link_status) !== -1 ? this.copy('link.' + item.link_status) : ''
      }
    },

    whoText (item, kind, created) {
      if (kind === 'password_changed') {
        return this.copy('who.changed', { datetime: created })
      }
      if (kind === 'reset_self') {
        return this.copy('who.self', { datetime: created })
      }
      if (item.requested_by && item.requested_by.name) {
        return this.copy('who.sentBy', { name: item.requested_by.name, datetime: created })
      }

      return this.copy('who.sent', { datetime: created })
    },

    // Status is never colour alone: every tag has an icon and a word.
    tag (item) {
      if (item.status === 'queued') {
        return { type: '', icon: 'fa-clock', text: this.copy('status.queued') }
      }
      if (item.status === 'failed') {
        return { type: 'danger', icon: 'fa-times-circle', text: this.copy('status.failed') }
      }
      if (item.status === 'blocked') {
        return { type: 'info', icon: 'fa-minus-circle', text: this.copy('status.blocked') }
      }
      // "Sent" on a capture host would claim a delivery that never happened.
      if (item.captured === true) {
        return { type: 'info', icon: 'fa-inbox', text: this.copy('status.captured') }
      }

      return { type: 'success', icon: 'fa-check', text: this.copy('status.sent') }
    },

    detail (item) {
      if (item.status === 'queued') {
        return this.copy('status.queued.detail')
      }
      if (item.status === 'failed') {
        return this.copy('status.failed.detail')
      }
      // A blocked row only ever carries spa_url_missing (D5: nothing else blocks).
      if (item.status === 'blocked') {
        return this.copy('status.blocked.detail')
      }
      if (item.captured === true) {
        return this.copy('status.captured.detail')
      }

      return this.copy('status.sent.detail', { datetime: item.sent_at ? this.formatDateTime(item.sent_at) : '' })
    },

    // Safari does not focus a button on click, so focus it first: the dialog records
    // document.activeElement when it opens and returns focus to it on close.
    openPreview (row, event) {
      var el = event && event.currentTarget
      var self = this

      if (el && typeof el.focus === 'function') {
        el.focus()
      }

      this.previewLast = row
      this.$nextTick(function () {
        if (self.previewLast === row) {
          self.previewRow = row
        }
      })
    }
  }
}
</script>

<style scoped>
/* The drawer body is read text, left-aligned. */
.account-mail { text-align: left; }
.account-mail__head { display: flex; justify-content: space-between; align-items: center; }
.account-mail__body { margin-top: 8px; }
.account-mail__body.is-loading { min-height: 120px; }
.account-mail__notice { margin-bottom: 10px; }
.account-mail__empty { margin: 8px 0; color: #606266; font-size: 13px; }
.account-mail__retry { margin-top: 8px; }
.account-mail__recent { display: block; margin-top: 6px; }

/* The recipient table of the request e-mail dialog (MailBatchDialog), the same classes. */
.mail-batch__queued { margin: 0 0 8px; }
.mail-batch__queued small { font-size: 12px; color: #606266; }
.mail-batch__queued ::v-deep .fa + span { margin-left: 5px; }
/* padding-bottom: el-table's .cell is overflow:hidden and this is its last line, so the 2px ring (+2px offset) would lose its bottom edge. */
.mail-batch__preview { margin-top: 2px; padding-bottom: 4px; line-height: 1; }
.mail-batch__preview ::v-deep .fa + span { margin-left: 5px; }
.mail-batch__preview ::v-deep .el-button:focus-visible { outline: 2px solid #409EFF; outline-offset: 2px; border-radius: 2px; }
.mail-batch__line { display: block; }
.mail-batch__email { display: block; font-size: 13px; color: #303133; word-break: break-all; }
.mail-batch__org { font-size: 12px; line-height: 18px; color: #606266; }
.mail-batch__aux { font-size: 12px; line-height: 18px; color: #909399; }

.mail-status__detail { margin-left: 6px; font-size: 12px; color: #606266; }
.mail-status .el-tag .fa { margin-right: 5px; }
/* The second detail line (link status) sits on its own line. */
.mail-status .mail-status__detail.is-block { display: block; margin: 2px 0 0; }
.mail-status--stacked { margin-top: 4px; }
.mail-status--stacked .mail-status__detail { margin-left: 0; display: block; margin-top: 2px; }
.mail-batch__table ::v-deep .cell { word-break: normal; overflow-wrap: break-word; }
.mail-batch__table ::v-deep .el-table__body tr.is-failed > td { background: #FEF0F0; }
.mail-batch__table ::v-deep .el-table__body tr.is-failed:hover > td { background: #FEF0F0; }
.mail-batch__table ::v-deep .el-table__body tr.is-failed > td:first-child { box-shadow: inset 3px 0 0 #F56C6C; }

.info-footnote { margin: 12px 0 0; font-size: 12px; line-height: 18px; color: #909399; }
.info-footnote .fa { margin-right: 6px; }
</style>
